import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SensitiveAmount from "components/sensitive-amount/SensitiveAmount";
import { formatDay } from "./personnel-helpers";

/** Small building blocks shared by the card's tabs. */

export const panelClass = "rounded-lg border border-gray-200 bg-white p-4 shadow-sm";

export function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className="text-right text-gray-900">{value == null || value === "" ? "—" : value}</span>
    </div>
  );
}

/** The «Planned change» line of the terms: a second salary that applies from a date. */
export function PlannedSalaryChangeFact({ change }: { change: { date: string | number; amount: number } }) {
  const { t } = useTranslation();
  return (
    <Fact
      label={t("personnel_planned_salary_change")}
      value={
        <Trans
          i18nKey="personnel_planned_salary_change_value"
          values={{ date: formatDay(change.date) }}
          components={{ amount: <SensitiveAmount value={change.amount} /> }}
        />
      }
    />
  );
}

/**
 * Prev/Next pager for a tab's table («Komandiruotės» / «Kuro ataskaitos» /
 * «Išlaidos» — the same footer as the employees list, one component so the
 * three tabs don't each grow their own copy). Hides itself when everything
 * fits on one page.
 */
export function TablePagination({
  page,
  limit,
  total,
  onPageChange,
  loading,
}: {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  if (total <= limit) return null;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-sm text-gray-600">
      <span>
        {t("personnel_pagination", {
          from: (page - 1) * limit + 1,
          to: Math.min(page * limit, total),
          total,
        })}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="inline-flex items-center rounded-md p-1 hover:bg-gray-100 disabled:opacity-40 max-md:p-3"
          disabled={page <= 1 || loading}
          onClick={() => onPageChange(page - 1)}
          aria-label={t("personnel_page_previous")}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          className="inline-flex items-center rounded-md p-1 hover:bg-gray-100 disabled:opacity-40 max-md:p-3"
          disabled={page >= pageCount || loading}
          onClick={() => onPageChange(page + 1)}
          aria-label={t("next")}
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
